import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { forkJoin } from 'rxjs';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ContractsService } from '../../contracts/contracts.service';
import { DeliveryRateCard } from '../../contracts/models/delivery-rate-card.model';
import { ContractRevisions } from '../../contracts/models/contract-revisions.model';
import { Contracts } from '../../contracts/models/contracts.model';
import { VehicleCapacity } from '../../fleet/vehicle/params/vehicle-capacity';
import { VehicleConfig } from '../../fleet/vehicle/params/vehicle-config';
import { VehicleType } from '../../fleet/vehicle/params/vehicle-type';
import { Country } from '../../models/common/country.model';
import { City } from '../../models/common/city.model';
import { BaseMasterDataService } from '../../services/base-master-data.service';
import { SessionStorageService } from '../../services/session-storage.service';
import { PermissionService } from '../../security/permission.service';
import { app_permissions } from '../../security/app-permissions';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import {
  DeliveryRateCardImportBatch,
  DeliveryRateCardImportPreview,
  DeliveryRateCardImportRow,
  DeliveryRateCardImportStatus
} from './delivery-rate-card-workspace.model';
import { DeliveryRateCardWorkspaceService } from './delivery-rate-card-workspace.service';

type RateRole = 'seller' | 'buyer';

interface ClientRateGroup {
  clientId?: number;
  clientName: string;
  roleLabel: string;
  totalAmount: number;
  rates: DeliveryRateCard[];
}

@Component({
  selector: 'app-delivery-rate-cards',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ToastModule,
    BreadcrumbModule,
    ButtonModule,
    DialogModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputNumberModule,
    InputTextModule,
    ProgressBarModule,
    SelectModule,
    TableModule,
    TabsModule,
    TagModule,
    ToggleSwitchModule,
    PageShellComponent,
    PageHeroComponent,
    PageFormSectionComponent,
    PageMetricStripComponent,
    PageDataPanelComponent
  ],
  providers: [BaseMasterDataService, MessageService],
  templateUrl: './delivery-rate-cards.component.html',
  styleUrls: ['./delivery-rate-cards.component.css']
})
export class DeliveryRateCardsComponent implements OnInit {
  readonly importColumns = [
    'sourceCountry', 'sourceCity', 'destinationCountry', 'destinationCity',
    'vehicleType', 'vehicleConfig', 'vehicleCapacity', 'distance', 'amount',
    'goodsInsurance', 'portPermit', 'waitingThreshold', 'waitingCharge'
  ];

  breadcrumbItems: MenuItem[] = [{ label: 'Shipments', routerLink: '/shipment/company' }, { label: 'Delivery Rate Cards' }];
  homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };

  contracts: Contracts[] = [];
  countries: Country[] = [];
  vehicleTypes: VehicleType[] = [];
  vehicleConfigs: VehicleConfig[] = [];
  vehicleCapacities: VehicleCapacity[] = [];
  private readonly cityOptionsByCountry = new Map<number, City[]>();
  private readonly cityDirectory = new Map<number, City>();

  rateCards: DeliveryRateCard[] = [];
  activeRateRole: RateRole = 'seller';
  totalRecords = 0;
  page = 0;
  pageSize = 50;
  loading = false;
  saving = false;
  processing = false;

  filterContractId?: number;
  filterRevisionId?: number;
  filterRevisionStatus?: string;
  filterSourceCountryId?: number;
  filterSourceCityId?: number;
  filterDestinationCountryId?: number;
  filterDestinationCityId?: number;
  filterVehicleTypeId?: number;
  filterVehicleConfigId?: number;
  filterVehicleCapacityId?: number;
  keyword = '';

  formVisible = false;
  rateForm: DeliveryRateCard = new DeliveryRateCard();
  formContract?: Contracts;
  formRevision?: ContractRevisions;
  formRevisions: ContractRevisions[] = [];
  formVehicleConfigs: VehicleConfig[] = [];
  formVehicleCapacities: VehicleCapacity[] = [];

  importContract?: Contracts;
  importRevision?: ContractRevisions;
  importRevisions: ContractRevisions[] = [];
  importFile?: File;
  currentImport?: DeliveryRateCardImportPreview;
  importBatches: DeliveryRateCardImportBatch[] = [];

  editRowDialogVisible = false;
  editingRow?: DeliveryRateCardImportRow;
  editingValues: Record<string, string> = {};

  revisionStatusOptions = [
    { label: 'All statuses', value: undefined },
    { label: 'Draft', value: 'DRAFT' },
    { label: 'Submitted', value: 'SUBMITTED' },
    { label: 'Approved', value: 'APPROVED' },
    { label: 'Pending Signature', value: 'PENDING_SIGNATURE' },
    { label: 'Active', value: 'ACTIVE' }
  ];

  constructor(
    private readonly route: ActivatedRoute,
    private readonly contractsService: ContractsService,
    private readonly rateCardService: DeliveryRateCardWorkspaceService,
    private readonly masterDataService: BaseMasterDataService,
    private readonly session: SessionStorageService,
    private readonly permissions: PermissionService,
    private readonly messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.vehicleTypes = this.session.getVehicleTypes() ?? [];
    this.vehicleConfigs = this.vehicleTypes.flatMap(type => type.vehicleConfigs || []);
    this.vehicleCapacities = this.vehicleTypes.flatMap(type => type.vehicleCapacities || []);
    this.countries = this.session.getCountryList() ?? [];
    this.loadContracts();
  }

  get canCreate(): boolean {
    return this.permissions.hasPermission(app_permissions.contracts.rate_cards.create);
  }

  get canEdit(): boolean {
    return this.permissions.hasPermission(app_permissions.contracts.rate_cards.edit);
  }

  get canDelete(): boolean {
    return this.permissions.hasPermission(app_permissions.contracts.rate_cards.delete);
  }

  get filterRevisions(): ContractRevisions[] {
    return this.contracts.find(contract => contract.contractID === this.filterContractId)?.revisions ?? [];
  }

  get activeRateGroups(): ClientRateGroup[] {
    return this.groupRatesByClient(this.rateCards);
  }

  get counterpartyLabel(): string {
    return this.activeRateRole === 'seller' ? 'Buyer Client' : 'Seller Client';
  }

  get formSourceCities(): City[] {
    return this.citiesForCountry(this.rateForm.sourceCountryId);
  }

  get formDestinationCities(): City[] {
    return this.citiesForCountry(this.rateForm.destinationCountryId);
  }

  get filterSourceCities(): City[] {
    return this.citiesForCountry(this.filterSourceCountryId);
  }

  get filterDestinationCities(): City[] {
    return this.citiesForCountry(this.filterDestinationCountryId);
  }

  get filteredRows(): DeliveryRateCardImportRow[] {
    return this.currentImport?.rows ?? [];
  }

  metrics(): PageMetric[] {
    const rows = this.currentImport?.rows || [];
    return [
      { label: 'Rate Cards', value: this.totalRecords, hint: 'Matching current filters', tone: 'info' },
      { label: 'Valid Import Rows', value: this.currentImport?.batch?.validRows || 0, hint: `${rows.length} staged`, tone: 'success' },
      { label: 'Invalid Rows', value: this.currentImport?.batch?.invalidRows || 0, hint: 'Need correction', tone: 'warn' },
      { label: 'Processed', value: this.currentImport?.batch?.successRows || 0, hint: `${this.currentImport?.batch?.failedRows || 0} failed`, tone: 'default' }
    ];
  }

  loadContracts(): void {
    const companyId = this.currentCompanyId();
    if (!companyId) {
      this.contractsService.getAllContracts(0, 500).subscribe({
        next: response => this.handleContractsLoaded([response]),
        error: error => this.handleContractsLoadError(error)
      });
      return;
    }

    forkJoin([
      this.contractsService.getAllContracts(0, 500, companyId),
      this.contractsService.getAllContracts(0, 500, undefined, companyId)
    ]).subscribe({
      next: responses => this.handleContractsLoaded(responses),
      error: error => this.handleContractsLoadError(error)
    });
  }

  private handleContractsLoaded(responses: Array<{ items?: Contracts[] }>): void {
    this.contracts = this.mergeContracts(responses.flatMap(response => response.items || []));
    const contractId = Number(this.route.snapshot.queryParamMap.get('contractId') || 0) || undefined;
    const revisionId = Number(this.route.snapshot.queryParamMap.get('revisionId') || 0) || undefined;
    this.filterContractId = contractId;
    this.filterRevisionId = revisionId;
    this.ensureCitiesForCountry(this.filterSourceCountryId);
    this.ensureCitiesForCountry(this.filterDestinationCountryId);
    this.primeImportSelection();
    this.loadRateCards();
    this.loadBatches();
  }

  private handleContractsLoadError(error: any): void {
    this.contracts = [];
    this.messageService.add({ severity: 'error', summary: 'Contracts failed', detail: error?.error?.message || error?.error || error.message });
    this.loadRateCards();
    this.loadBatches();
  }

  private mergeContracts(contracts: Contracts[]): Contracts[] {
    const byId = new Map<number, Contracts>();
    contracts.forEach(contract => {
      if (contract.contractID) {
        byId.set(contract.contractID, contract);
      }
    });
    return [...byId.values()].sort((a, b) => (a.contractNumber || '').localeCompare(b.contractNumber || ''));
  }

  loadRateCards(): void {
    const companyId = this.currentCompanyId();
    this.loading = true;
    this.rateCardService.list({
      contractId: this.filterContractId,
      revisionId: this.filterRevisionId,
      sellingCompanyId: this.activeRateRole === 'seller' ? companyId : undefined,
      buyingCompanyId: this.activeRateRole === 'buyer' ? companyId : undefined,
      sourceCountryId: this.filterSourceCountryId,
      sourceCityId: this.filterSourceCityId,
      destinationCountryId: this.filterDestinationCountryId,
      destinationCityId: this.filterDestinationCityId,
      vehicleTypeId: this.filterVehicleTypeId,
      vehicleConfigId: this.filterVehicleConfigId,
      vehicleCapacityId: this.filterVehicleCapacityId,
      revisionStatus: this.filterRevisionStatus,
      keyword: this.keyword,
      page: this.page,
      pageSize: this.pageSize
    }).subscribe({
      next: response => {
        this.rateCards = response.items || [];
        this.totalRecords = response.count || this.rateCards.length;
        this.loading = false;
      },
      error: error => {
        this.loading = false;
        this.messageService.add({ severity: 'error', summary: 'Load failed', detail: error?.error?.message || error.message });
      }
    });
  }

  applyFilters(): void {
    this.page = 0;
    this.loadRateCards();
    this.primeImportSelection();
    this.loadBatches();
  }

  clearFilters(): void {
    this.filterContractId = undefined;
    this.filterRevisionId = undefined;
    this.filterRevisionStatus = undefined;
    this.filterSourceCountryId = undefined;
    this.filterSourceCityId = undefined;
    this.filterDestinationCountryId = undefined;
    this.filterDestinationCityId = undefined;
    this.filterVehicleTypeId = undefined;
    this.filterVehicleConfigId = undefined;
    this.filterVehicleCapacityId = undefined;
    this.keyword = '';
    this.applyFilters();
  }

  onRateRoleChange(role: unknown): void {
    const nextRole: RateRole = role === 'buyer' ? 'buyer' : 'seller';
    if (this.activeRateRole === nextRole) {
      return;
    }
    this.activeRateRole = nextRole;
    this.page = 0;
    this.loadRateCards();
  }

  contractDisplay(rate: DeliveryRateCard): string {
    return this.firstText(rate.contractNumber) || 'Unlinked contract';
  }

  contractDisplayLabel(contract?: Contracts): string {
    const contractNumber = this.firstText(contract?.contractNumber) || 'Unnamed contract';
    const parties = this.contractPartiesLabel(contract);
    return parties ? `${contractNumber} — ${parties}` : contractNumber;
  }

  contractPartiesLabel(contract?: Contracts): string {
    const firstParty = this.firstText(contract?.firstParty?.name);
    const secondParty = this.firstText(contract?.secondParty?.name);
    return [firstParty, secondParty].filter(Boolean).join(' → ');
  }

  revisionDisplay(rate: DeliveryRateCard): string {
    const revisionNumber = this.firstText(rate.revisionNumber);
    return revisionNumber ? `Revision ${revisionNumber}` : 'No revision';
  }

  onFilterContractChange(): void {
    this.filterRevisionId = undefined;
    this.applyFilters();
  }

  onFilterSourceCountryChange(): void {
    if (!this.cityBelongsToCountry(this.filterSourceCityId, this.filterSourceCountryId)) {
      this.filterSourceCityId = undefined;
    }
    this.ensureCitiesForCountry(this.filterSourceCountryId);
    this.applyFilters();
  }

  onFilterDestinationCountryChange(): void {
    if (!this.cityBelongsToCountry(this.filterDestinationCityId, this.filterDestinationCountryId)) {
      this.filterDestinationCityId = undefined;
    }
    this.ensureCitiesForCountry(this.filterDestinationCountryId);
    this.applyFilters();
  }

  onFilterVehicleTypeChange(): void {
    this.filterVehicleConfigId = undefined;
    this.filterVehicleCapacityId = undefined;
    this.applyFilters();
  }

  openCreate(): void {
    this.rateForm = new DeliveryRateCard();
    this.formContract = this.contracts.find(contract => contract.contractID === this.filterContractId);
    this.formRevisions = this.formContract?.revisions ?? [];
    this.formRevision = this.formRevisions.find(revision => revision.revisionID === this.filterRevisionId);
    this.formVehicleConfigs = [];
    this.formVehicleCapacities = [];
    this.ensureCitiesForCountry(this.rateForm.sourceCountryId);
    this.ensureCitiesForCountry(this.rateForm.destinationCountryId);
    this.formVisible = true;
  }

  openEdit(rateCard: DeliveryRateCard): void {
    this.rateForm = { ...rateCard } as DeliveryRateCard;
    this.formContract = this.contracts.find(contract => contract.contractID === rateCard.contractId);
    this.formRevisions = this.formContract?.revisions ?? [];
    this.formRevision = this.formRevisions.find(revision => revision.revisionID === rateCard.contractRevisionId);
    this.updateFormVehicleOptions();
    this.ensureCitiesForCountry(this.rateForm.sourceCountryId);
    this.ensureCitiesForCountry(this.rateForm.destinationCountryId);
    this.formVisible = true;
  }

  saveRateCard(): void {
    if (!this.formContract?.contractID || !this.formRevision?.revisionID) {
      this.messageService.add({ severity: 'warn', summary: 'Missing selection', detail: 'Select a contract and revision.' });
      return;
    }
    const sourceCity = this.cityById(this.rateForm.sourceCityId);
    const destinationCity = this.cityById(this.rateForm.destinationCityId);
    const sourceCountry = this.countryById(this.rateForm.sourceCountryId);
    const destinationCountry = this.countryById(this.rateForm.destinationCountryId);
    this.rateForm.sourceCountryId = this.cityCountryId(sourceCity) ?? this.rateForm.sourceCountryId;
    this.rateForm.destinationCountryId = this.cityCountryId(destinationCity) ?? this.rateForm.destinationCountryId;
    this.rateForm.sourceCity = sourceCity ? this.cityLabel(sourceCity) : undefined;
    this.rateForm.sourceCountry = this.cityCountryName(sourceCity) ?? sourceCountry?.name;
    this.rateForm.destinationCity = destinationCity ? this.cityLabel(destinationCity) : undefined;
    this.rateForm.destinationCountry = this.cityCountryName(destinationCity) ?? destinationCountry?.name;
    // The toggles render an undefined value as "Excluded", but the API has
    // non-null columns for both fields. Normalize older/incomplete form data
    // before it is sent so an untouched toggle is persisted as false.
    this.rateForm.goodsInsurance = this.rateForm.goodsInsurance ?? false;
    this.rateForm.portPermit = this.rateForm.portPermit ?? false;
    this.saving = true;
    const request = this.rateForm.rateId
      ? this.rateCardService.update(this.rateForm.rateId, this.formContract.contractID, this.formRevision.revisionID, this.rateForm)
      : this.rateCardService.create(this.formContract.contractID, this.formRevision.revisionID, this.rateForm);
    request.subscribe({
      next: () => {
        this.saving = false;
        this.formVisible = false;
        this.loadRateCards();
        this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Delivery rate card saved.' });
      },
      error: error => {
        this.saving = false;
        this.messageService.add({ severity: 'error', summary: 'Save failed', detail: error?.error?.message || error?.error || error.message });
      }
    });
  }

  deleteRateCard(rateCard: DeliveryRateCard): void {
    if (!rateCard.rateId || !confirm(`Delete rate card #${rateCard.rateId}?`)) return;
    this.rateCardService.delete(rateCard.rateId).subscribe({
      next: () => {
        this.loadRateCards();
        this.messageService.add({ severity: 'success', summary: 'Deleted', detail: 'Delivery rate card deleted.' });
      },
      error: error => this.messageService.add({ severity: 'error', summary: 'Delete failed', detail: error?.error?.message || error.message })
    });
  }

  onFormContractChange(): void {
    this.formRevisions = this.formContract?.revisions ?? [];
    this.formRevision = undefined;
  }

  onFormVehicleTypeChange(): void {
    this.rateForm.vehicleConfigId = undefined as any;
    this.rateForm.vehicleCapacityId = undefined as any;
    this.updateFormVehicleOptions();
  }

  onFormSourceCountryChange(): void {
    if (this.rateForm.sourceCityId && !this.formSourceCities.some(city => city.id === this.rateForm.sourceCityId)) {
      this.rateForm.sourceCityId = undefined;
      this.rateForm.sourceCity = undefined;
    }
    this.ensureCitiesForCountry(this.rateForm.sourceCountryId);
    this.rateForm.sourceCountry = this.countryById(this.rateForm.sourceCountryId)?.name;
    this.updateFormDistance();
  }

  onFormDestinationCountryChange(): void {
    if (this.rateForm.destinationCityId && !this.formDestinationCities.some(city => city.id === this.rateForm.destinationCityId)) {
      this.rateForm.destinationCityId = undefined;
      this.rateForm.destinationCity = undefined;
    }
    this.ensureCitiesForCountry(this.rateForm.destinationCountryId);
    this.rateForm.destinationCountry = this.countryById(this.rateForm.destinationCountryId)?.name;
    this.updateFormDistance();
  }

  onFormSourceCityChange(): void {
    const city = this.cityById(this.rateForm.sourceCityId);
    this.rateForm.sourceCountryId = this.cityCountryId(city) ?? this.rateForm.sourceCountryId;
    this.rateForm.sourceCity = city ? this.cityLabel(city) : undefined;
    this.rateForm.sourceCountry = this.cityCountryName(city) ?? this.rateForm.sourceCountry;
    this.updateFormDistance();
  }

  onFormDestinationCityChange(): void {
    const city = this.cityById(this.rateForm.destinationCityId);
    this.rateForm.destinationCountryId = this.cityCountryId(city) ?? this.rateForm.destinationCountryId;
    this.rateForm.destinationCity = city ? this.cityLabel(city) : undefined;
    this.rateForm.destinationCountry = this.cityCountryName(city) ?? this.rateForm.destinationCountry;
    this.updateFormDistance();
  }

  private updateFormDistance(): void {
    const source = this.cityById(this.rateForm.sourceCityId)?.coordinates;
    const destination = this.cityById(this.rateForm.destinationCityId)?.coordinates;
    if (source?.latitude == null || source?.longitude == null
      || destination?.latitude == null || destination?.longitude == null) {
      this.rateForm.distance = undefined as any;
      return;
    }
    const toRadians = (value: number) => value * Math.PI / 180;
    const latitudeDelta = toRadians(destination.latitude - source.latitude);
    const longitudeDelta = toRadians(destination.longitude - source.longitude);
    const haversine = Math.sin(latitudeDelta / 2) ** 2
      + Math.cos(toRadians(source.latitude)) * Math.cos(toRadians(destination.latitude))
      * Math.sin(longitudeDelta / 2) ** 2;
    this.rateForm.distance = Math.round(2 * 6371.0088 * Math.asin(Math.sqrt(haversine)) * 100) / 100;
  }

  updateFormVehicleOptions(): void {
    this.formVehicleConfigs = this.vehicleConfigs.filter(config => config.typeId === this.rateForm.vehicleTypeId);
    this.formVehicleCapacities = this.vehicleCapacities.filter(capacity => capacity.typeId === this.rateForm.vehicleTypeId);
  }

  onImportContractChange(): void {
    this.importRevisions = this.importContract?.revisions ?? [];
    this.importRevision = undefined;
    this.currentImport = undefined;
    this.loadBatches();
  }

  onImportRevisionChange(): void {
    this.currentImport = undefined;
    this.loadBatches();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.importFile = input.files?.[0] || undefined;
  }

  uploadImport(): void {
    if (!this.importFile || !this.importContract?.contractID || !this.importRevision?.revisionID) {
      this.messageService.add({ severity: 'warn', summary: 'Missing data', detail: 'Select contract, revision, and file.' });
      return;
    }
    this.loading = true;
    this.rateCardService.upload(this.importFile, this.importContract.contractID, this.importRevision.revisionID, this.session.getCompany()?.id).subscribe({
      next: response => {
        this.currentImport = this.preparePreview(response.data);
        this.loading = false;
        this.loadBatches();
        this.messageService.add({ severity: 'success', summary: 'Uploaded', detail: 'Rows are ready for review.' });
      },
      error: error => {
        this.loading = false;
        this.messageService.add({ severity: 'error', summary: 'Upload failed', detail: error?.error?.message || error?.error || error.message });
      }
    });
  }

  downloadTemplate(format: 'xlsx' | 'csv'): void {
    if (!this.importContract?.contractID || !this.importRevision?.revisionID) {
      this.messageService.add({ severity: 'warn', summary: 'Missing selection', detail: 'Select a contract and revision first.' });
      return;
    }
    this.loading = true;
    this.rateCardService.template(this.importContract.contractID, this.importRevision.revisionID, format)
      .subscribe({
        next: response => {
          this.loading = false;
          this.saveBlob(response.body!, `delivery-rate-card-import-template.${format}`);
        },
        error: error => {
          this.loading = false;
          this.messageService.add({ severity: 'error', summary: 'Template failed', detail: error?.error?.message || error?.error || error.message });
        }
      });
  }

  loadBatches(): void {
    this.rateCardService.batches(this.importContract?.contractID, this.importRevision?.revisionID, this.session.getCompany()?.id)
      .subscribe(response => this.importBatches = response.items || []);
  }

  openBatch(batch: DeliveryRateCardImportBatch): void {
    this.loading = true;
    this.rateCardService.batch(batch.batchId).subscribe({
      next: response => {
        this.currentImport = this.preparePreview(response.data);
        this.loading = false;
      },
      error: () => this.loading = false
    });
  }

  processBatch(): void {
    if (!this.currentImport?.batch?.batchId) return;
    this.processing = true;
    this.rateCardService.processBatch(this.currentImport.batch.batchId).subscribe({
      next: response => {
        this.currentImport = this.preparePreview({ batch: response.data.batch, rows: response.data.rows });
        this.processing = false;
        this.loadRateCards();
        this.loadBatches();
        this.messageService.add({ severity: 'success', summary: 'Processed', detail: 'Valid rate-card rows were processed.' });
      },
      error: error => {
        this.processing = false;
        this.messageService.add({ severity: 'error', summary: 'Process failed', detail: error?.error?.message || error.message });
      }
    });
  }

  retryRow(row: DeliveryRateCardImportRow): void {
    this.rateCardService.processRow(row.rowId).subscribe({
      next: response => {
        Object.assign(row, this.prepareRow(response.data));
        this.loadRateCards();
      },
      error: error => this.messageService.add({ severity: 'error', summary: 'Retry failed', detail: error?.error?.message || error.message })
    });
  }

  openEditRow(row: DeliveryRateCardImportRow): void {
    this.editingRow = this.prepareRow({ ...row, values: { ...(row.values || {}) } });
    this.editingValues = { ...(this.editingRow.values || {}) };
    this.editRowDialogVisible = true;
  }

  saveRow(): void {
    if (!this.editingRow) return;
    this.editingRow.values = this.editingValues;
    this.rateCardService.updateRow(this.editingRow).subscribe({
      next: response => {
        const index = this.currentImport?.rows.findIndex(row => row.rowId === response.data.rowId) ?? -1;
        if (this.currentImport && index >= 0) this.currentImport.rows[index] = this.prepareRow(response.data);
        this.editRowDialogVisible = false;
        this.refreshCurrentBatch();
        this.messageService.add({ severity: 'success', summary: 'Updated', detail: 'Row was revalidated.' });
      },
      error: error => this.messageService.add({ severity: 'error', summary: 'Update failed', detail: error?.error?.message || error.message })
    });
  }

  exportFailedRows(): void {
    const headers = ['rowNumber', 'status', 'errorMessage', ...this.importColumns];
    const rows = (this.currentImport?.rows || [])
      .filter(row => row.status === 'FAILED' || row.status === 'INVALID')
      .map(row => {
        const record: Record<string, string | number | undefined> = {
          rowNumber: row.rowNumber,
          status: row.status,
          errorMessage: row.errorMessage
        };
        this.importColumns.forEach(column => {
          const value = this.rowImportValue(row, column);
          record[column] = value === '-' ? '' : value;
        });
        return record;
      });
    const csv = [headers.join(','), ...rows.map(row => headers.map(header => `"${String(row[header] ?? '').replace(/"/g, '""')}"`).join(','))]
      .join('\n');
    this.saveBlob(new Blob([csv], { type: 'text/csv' }), `delivery-rate-card-import-failed-${Date.now()}.csv`);
  }

  rowImportValue(row: DeliveryRateCardImportRow, ...keys: string[]): string {
    const normalized = this.parsePayload(row.normalizedPayload);
    const raw = this.parsePayload(row.rawPayload);
    const values = row.values || {};
    for (const key of keys) {
      const value = this.firstText(normalized[key], values[key], raw[key]);
      if (value) return value;
    }
    return '-';
  }

  cityById(cityId?: number): City | undefined {
    if (!cityId) {
      return undefined;
    }
    return this.cityDirectory.get(cityId);
  }

  countryById(countryId?: number): Country | undefined {
    return this.countries.find(country => country.id === countryId);
  }

  cityLabel(city?: City | number | null): string {
    if (typeof city === 'number') {
      return this.cityLabel(this.cityById(city));
    }
    if (!city) {
      return '';
    }
    const raw = city as City & Record<string, unknown>;
    return this.firstText(
      city.cityName,
      raw['displayName'],
      raw['name'],
      raw['city_name'],
      raw['cityNameEn'],
      raw['englishName'],
      raw['nameEn'],
      raw['localizedName'],
      raw['label'],
      raw['text'],
      raw['city'],
      raw['description']
    ) || (city.id ? `City #${city.id}` : 'Unnamed city');
  }

  countryNameForCity(city?: City | number | null): string {
    const cityOption = typeof city === 'number' ? this.cityById(city) : city;
    return this.cityCountryName(cityOption ?? undefined) ?? '';
  }

  countryLabelForRate(rate: DeliveryRateCard, direction: 'source' | 'destination'): string {
    const countryId = direction === 'source' ? rate.sourceCountryId : rate.destinationCountryId;
    const countryText = direction === 'source' ? rate.sourceCountry : rate.destinationCountry;
    return this.firstText(
      this.countryById(countryId)?.name,
      direction === 'source' ? rate.sourceCountryName : rate.destinationCountryName,
      countryText
    ) || '-';
  }

  private citiesForCountry(countryId?: number): City[] {
    if (!countryId) {
      return [];
    }
    return this.cityOptionsByCountry.get(countryId) ?? [];
  }

  vehicleTypeName(id?: number): string {
    return this.vehicleTypes.find(type => type.id === id)?.name || '-';
  }

  statusSeverity(status?: DeliveryRateCardImportStatus | string): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' | undefined {
    if (status === 'SUCCESS' || status === 'COMPLETED' || status === 'ACTIVE') return 'success';
    if (status === 'VALID' || status === 'APPROVED') return 'info';
    if (status === 'INVALID' || status === 'FAILED' || status === 'REJECTED') return 'danger';
    if (status === 'PARTIAL_SUCCESS' || status === 'DRAFT' || status === 'PENDING_SIGNATURE') return 'warn';
    return 'secondary';
  }

  private groupRatesByClient(rates: DeliveryRateCard[]): ClientRateGroup[] {
    const roleLabel = this.counterpartyLabel;
    const groups = new Map<string, ClientRateGroup>();
    rates.forEach(rate => {
      const clientId = this.activeRateRole === 'seller' ? rate.buyingCompanyId : rate.sellingCompanyId;
      const clientName = this.firstText(
        this.activeRateRole === 'seller' ? rate.buyingCompanyName : rate.sellingCompanyName,
        'Unassigned client'
      );
      const groupKey = clientId ? String(clientId) : `name:${clientName.toLowerCase()}`;
      const existing = groups.get(groupKey) ?? {
        clientId,
        clientName,
        roleLabel,
        totalAmount: 0,
        rates: []
      };
      existing.totalAmount += Number(rate.amount || 0);
      existing.rates.push(rate);
      groups.set(groupKey, existing);
    });
    return [...groups.values()].sort((a, b) => a.clientName.localeCompare(b.clientName));
  }

  private currentCompanyId(): number | undefined {
    return this.session.getCompany()?.id || undefined;
  }

  private primeImportSelection(): void {
    this.importContract = this.contracts.find(contract => contract.contractID === this.filterContractId) ?? this.importContract;
    this.importRevisions = this.importContract?.revisions ?? [];
    this.importRevision = this.importRevisions.find(revision => revision.revisionID === this.filterRevisionId) ?? this.importRevision;
  }

  private refreshCurrentBatch(): void {
    if (this.currentImport?.batch?.batchId) {
      this.rateCardService.batch(this.currentImport.batch.batchId)
        .subscribe(response => this.currentImport = this.preparePreview(response.data));
    }
  }

  private preparePreview(preview: DeliveryRateCardImportPreview): DeliveryRateCardImportPreview {
    return {
      batch: preview.batch,
      rows: (preview.rows || []).map(row => this.prepareRow(row))
    };
  }

  private prepareRow(row: DeliveryRateCardImportRow): DeliveryRateCardImportRow {
    row.values = row.values || this.parsePayload(row.rawPayload || row.normalizedPayload);
    return row;
  }

  private parsePayload(payload?: string): Record<string, string> {
    if (!payload) return {};
    try {
      const parsed = JSON.parse(payload);
      return Object.keys(parsed || {}).reduce((acc, key) => {
        acc[key] = parsed[key] === null || parsed[key] === undefined ? '' : String(parsed[key]);
        return acc;
      }, {} as Record<string, string>);
    } catch {
      return {};
    }
  }

  private normalizeCity(city: City): City {
    const raw = city as City & Record<string, unknown>;
    const cityId = this.numberValue(city.id) ?? this.numberValue(raw['cityId']) ?? this.numberValue(raw['cityID']);
    const countryId = this.cityCountryId(city);
    const cityWithId = {
      ...city,
      id: cityId ?? city.id
    };
    const normalized = {
      ...cityWithId,
      cityName: this.cityLabel(cityWithId)
    };
    if (!normalized.country && countryId) {
      const country = this.countryById(countryId);
      if (country) {
        normalized.country = country;
      }
    }
    return normalized;
  }

  private cityBelongsToCountry(cityId?: number, countryId?: number): boolean {
    if (!cityId || !countryId) {
      return false;
    }
    return this.cityCountryId(this.cityById(cityId)) === countryId;
  }

  private ensureCitiesForCountry(countryId?: number): void {
    if (!countryId || this.cityOptionsByCountry.has(countryId)) {
      return;
    }
    this.masterDataService.getCities('en-US', countryId).subscribe({
      next: cities => {
        const normalized = (cities ?? []).map(city => this.normalizeCity(city));
        this.cityOptionsByCountry.set(countryId, normalized);
        normalized.forEach(city => {
          if (city.id) {
            this.cityDirectory.set(city.id, city);
          }
        });
      },
      error: error => {
        this.messageService.add({
          severity: 'error',
          summary: 'Cities failed',
          detail: error?.error?.message || error?.error || error.message
        });
      }
    });
  }

  private cityCountryId(city?: City): number | undefined {
    if (!city) {
      return undefined;
    }
    const raw = city as City & Record<string, unknown>;
    const country = city.country as Country & Record<string, unknown>;
    return this.numberValue(country?.id)
      ?? this.numberValue(raw['countryId'])
      ?? this.numberValue(raw['countryID'])
      ?? this.numberValue(raw['country_id']);
  }

  private cityCountryName(city?: City): string | undefined {
    if (!city) {
      return undefined;
    }
    const raw = city as City & Record<string, unknown>;
    const countryId = this.cityCountryId(city);
    return this.firstText(
      city.country?.name,
      raw['countryName'],
      raw['country_name'],
      this.countryById(countryId)?.name
    ) || undefined;
  }

  private firstText(...values: unknown[]): string {
    return values
      .map(value => value === null || value === undefined ? '' : String(value).trim())
      .find(value => !!value) ?? '';
  }

  private numberValue(value: unknown): number | undefined {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  private saveBlob(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    window.URL.revokeObjectURL(url);
  }
}
